import React, { useEffect } from 'react';

import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';

import { getAdminDetails, getUserDetails } from '../services/api';
import { IShippingRate } from '../types/campaigns';
import { IUserContact } from '../types/identity';
import { IAccurredPointResponse } from '../types/loyalty';
import { ICustomer } from '../types/subscription';
import { ICardToken, TShiping } from '../types/walllet';

type TUser = {
  firstName: string;
  lastName: string;
  email: string;
  dob: string;
  userUniqueReferenceNumber: string;
  consumerUniqueReferenceNumber: string;
  walletUniqueReferenceNumber: string;
};

export type TPayLinkCardToken = {
  uniqueReferenceNumber: string;
  lastFourDigit?: string;
  cardType?: string;
  authorizationCode?: string;
  [key: string]: any;
};

export type TPayLinkReward = {
  uniqueReferenceNumber: string;
  rewardName?: string;
  rewardReason?: string;
  rewardEffect?: string;
  rewardEffectValue?: number | string;
  promotionUniqueReferenceNumber?: string;
  loyaltyProgrammeTierUniqueReferenceNumber?: string;
  isPartner?: boolean;
  partnerProgrammeUniqueReferenceNumber?: string;
  partnerCustomerUniqueReferenceNumber?: string;
  partnerMerchantUniqueReferenceNumber?: string;
  customerUniqueReferenceNumber?: string;
  [key: string]: any;
};

export type TPayLinkPointMerchant = {
  merchantUrn: string;
  points: number;
  isAdminMerchant?: boolean;
};

interface IPaylinkUserContext {
  user: TUser | null;
  deliveryNote: string;
  setDeliveryNote: React.Dispatch<React.SetStateAction<string>>;
  shippingAddress: IUserContact | null;
  handleSelectAddress: (address: IUserContact) => void;
  shippingRate: TShiping | null;
  handleSetShippingAddressAndRate: (rateObj: TShiping | null) => void;
  cardToken: ICardToken | null;
  reward: TPayLinkReward | null;
  freeShippingReward: TPayLinkReward | null;
  pointMerchant: IAccurredPointResponse | null;
  redeemedPoints: number;
  adminDetails: ICustomer | null;
  setCardToken: (token: ICardToken | null) => void;
  setReward: (reward: TPayLinkReward | null) => void;
  setFreeShippingReward: (reward: TPayLinkReward | null) => void;
  setPointMerchant: (merchant: IAccurredPointResponse | null) => void;
  setRedeemedPoints: (value: number) => void;
}

const PayLinkUserContext = React.createContext<IPaylinkUserContext | null>(
  null,
);

export const PayLinkUserProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { accounts, inProgress } = useMsal();
  const [user, setUser] = React.useState<TUser | null>(null);
  const [shippingAddress, setShippingAddress] =
    React.useState<IUserContact | null>(null);
  const [shippingRate, setShippingRate] = React.useState<TShiping | null>(null);
  const [deliveryNote, setDeliveryNote] = React.useState<string>("");
  const [cardToken, setCardToken] = React.useState<ICardToken | null>(null);
  const [reward, setReward] = React.useState<TPayLinkReward | null>(null);
  const [freeShippingReward, setFreeShippingReward] = React.useState<TPayLinkReward | null>(null);
  const [pointMerchant, setPointMerchant] =
    React.useState<IAccurredPointResponse | null>(null);
  const [redeemedPoints, setRedeemedPoints] = React.useState<number>(0);
  const [adminDetails, setAdminDetails] = React.useState<ICustomer | null>(
    null,
  );
  const { data: user_data } = useQuery({
    queryKey: ["GetUserDetails"],
    queryFn: () => getUserDetails(user?.userUniqueReferenceNumber ?? ""),
    enabled: !!user?.userUniqueReferenceNumber,
  });
  const { data: admin_data } = useQuery({
    queryKey: ["GetAdminDetails"],
    queryFn: () => getAdminDetails({ accountType: "Admin" }),
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    if (!user_data || !user) return;
    const nameParts = user_data.name.split(" ");
    setUser({
      ...user,
      firstName: nameParts[0] === "Unspecified" ? "" : nameParts[0],
      lastName: nameParts[1] === "Unspecified" ? "" : nameParts[1],
      dob: user_data.dateOfBirth,
      email: user_data.emailAddress
    });
  }, [user_data]);

  React.useEffect(() => {
    if (admin_data?.data && admin_data?.data.length > 0) {
      setAdminDetails(admin_data.data[0]);
    }
  }, [admin_data]);

  React.useEffect(() => {
    getToken();
  }, [inProgress, accounts]);

  const getToken = async () => {
    if (accounts && accounts.length > 0) {
      const _account = accounts[0];
      setUser({
        firstName:
          _account.idTokenClaims?.given_name &&
          _account.idTokenClaims?.given_name === "Unspecified"
            ? ""
            : ((_account.idTokenClaims?.given_name as string) ?? ""),
        lastName:
          _account.idTokenClaims?.family_name &&
          _account.idTokenClaims?.family_name === "Unspecified"
            ? ""
            : ((_account.idTokenClaims?.family_name as string) ?? ""),
        email: (_account.idTokenClaims?.email as string) ?? "",
        userUniqueReferenceNumber:
          (_account.idTokenClaims?.extension_UserURN as string) ?? "",
        consumerUniqueReferenceNumber:
          (_account.idTokenClaims?.extension_UserURN as string) ?? "",
        walletUniqueReferenceNumber:
          (_account.idTokenClaims?.sub as string) ?? "",
        dob: "",
      });
    }
  };

  const handleSelectAddress = (address: IUserContact) => {
    setShippingAddress(address);
  };

  const handleSetShippingAddressAndRate = (rateObj: TShiping | null) => {
    setShippingRate(rateObj);
  };

  console.log({user})

  return (
    <PayLinkUserContext.Provider
      value={{
        user,
        shippingAddress,
        handleSelectAddress,
        shippingRate,
        handleSetShippingAddressAndRate,
        cardToken,
        reward,
        freeShippingReward,
        pointMerchant,
        redeemedPoints,
        adminDetails,
        setCardToken,
        setReward,
        setFreeShippingReward,
        setPointMerchant,
        setRedeemedPoints,
        deliveryNote,
        setDeliveryNote,
      }}
    >
      {children}
    </PayLinkUserContext.Provider>
  );
};

export const usePayLinkUser = () => {
  const context = React.useContext(PayLinkUserContext);

  if (!context) {
    throw new Error("usePayLinkUser must be used within a PayLinkUserProvider");
  }

  return context;
};
